<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import {
	fetchMercariBrands,
	type IMercariBrand,
} from '@/assets/ts/apiManagement'
import { getMercariUrl } from '@/lib/mercari'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import getRedirectSeoLink from '@/lib/getSeoRedirect'
import SearchInput, {
	type SearchResult,
} from '@/components/ui/input/SearchInput.vue'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'
import type { IBreadcrumbItem } from '@/types/BreadCrumb'

interface BrandGroup {
	letter: string
	brands: IMercariBrand[]
}

const props = defineProps<{
	pageContent: {
		searchPlaceholder: string
		searchNoResultText: string
		breadcrumbs: {
			home: string
			shopping: string
			mercari: string
			brandList: string
		}
	}
}>()

const brands = ref<IMercariBrand[]>([])
const isLoading = ref(true)
const isError = ref(false)
const searchQuery = ref('')

const breadcrumbs = computed<IBreadcrumbItem[]>(() => [
	{
		displayName: props.pageContent.breadcrumbs.home,
		categoryId: 'home',
		link: getSiteBaseUrl(),
	},
	{
		displayName: props.pageContent.breadcrumbs.shopping,
		categoryId: 'shopping',
		link: `${getSiteBaseUrl()}/${getRedirectSeoLink('proxyShopping')}/`,
	},
	{
		displayName: props.pageContent.breadcrumbs.mercari,
		categoryId: 'mercari',
		link: getMercariUrl('index'),
	},
	{
		displayName: props.pageContent.breadcrumbs.brandList,
		categoryId: 'brands',
		link: '',
	},
])

const brandSearchOptions = computed<SearchResult[]>(() => {
	return brands.value.map(brand => ({
		id: brand.slug,
		text: brand.name,
	}))
})

const groupedBrands = computed<BrandGroup[]>(() => {
	if (brands.value.length === 0) return []

	const groups: { [key: string]: IMercariBrand[] } = {}

	brands.value.forEach(brand => {
		const firstChar = brand.name.charAt(0).toUpperCase()
		const letter = /[A-Z]/.test(firstChar) ? firstChar : '#'

		if (!groups[letter]) {
			groups[letter] = []
		}
		groups[letter].push(brand)
	})

	// Sort groups: A-Z first, then #
	const sortedKeys = Object.keys(groups).sort((a, b) => {
		if (a === '#') return 1
		if (b === '#') return -1
		return a.localeCompare(b)
	})

	return sortedKeys.map(letter => ({
		letter,
		brands: groups[letter].sort((a, b) => a.name.localeCompare(b.name)),
	}))
})

const fetchBrands = async () => {
	try {
		isLoading.value = true
		isError.value = false
		brands.value = await fetchMercariBrands()
	} catch (error) {
		console.error('Failed to fetch brands:', error)
		isError.value = true
	} finally {
		isLoading.value = false
	}
}

const handleBrandClick = (brand: IMercariBrand) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariBrands,
		button_name: 'Brand name',
		event_extra: brand.brand_id,
	})
}

const handleSearchResultClick = (result: SearchResult) => {
	const brand = brands.value.find(b => b.slug === result.id)
	if (brand) {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: GaGeneralPageName.MercariBrands,
			button_name: 'Brand name-search',
			event_extra: brand.brand_id,
		})
		window.open(getBrandUrl(brand), '_blank', 'noopener noreferrer')
	}
}

const handleNoResultClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariBrands,
		button_name: 'Free Text Search',
		search_query: searchQuery.value,
	})

	window.location.href = getMercariUrl('search', {
		searchQuery: searchQuery.value,
	})
}

const handleSearchInputFocus = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariBrands,
		button_name: 'Search Field',
	})
}

const getBrandUrl = (brand: IMercariBrand) => {
	return getMercariUrl('brand-listing', { brand_slug: brand.slug ?? '' })
}

onMounted(() => {
	fetchBrands()

	createGAEvent(GaGeneralEvent.PageLoad, {
		page_name: GaGeneralPageName.MercariBrands,
	})
})
</script>

<template>
	<div id="mercari-brands" class="min-h-screen bg-white">
		<!-- Breadcrumb -->
		<div class="bs-container pt-24px lg:pt-32px">
			<div class="col-span-12">
				<PageBreadCrumb
					:breadcrumbs="breadcrumbs"
					:is-override-seo-check="true"
					class="p-0"
					is-last-item-self
				/>
			</div>
		</div>

		<!-- Search Bar -->
		<div
			v-if="brandSearchOptions.length > 0"
			class="bs-container py-24px lg:max-w-[796px] lg:pb-32px lg:pt-16px"
		>
			<div class="col-span-12">
				<SearchInput
					v-model="searchQuery"
					:enable-dropdown="true"
					:options="brandSearchOptions"
					:placeholder="pageContent.searchPlaceholder"
					:no-result-text="pageContent.searchNoResultText"
					:is-desktop-search-icon-hidden="true"
					@result-click="handleSearchResultClick"
					@no-result-click="handleNoResultClick"
					@focus="handleSearchInputFocus"
				/>
			</div>
		</div>

		<!-- Brand List -->
		<div
			v-if="groupedBrands.length > 0"
			class="bs-container pb-24px lg:max-w-[796px] lg:pb-32px"
		>
			<div class="col-span-12">
				<div
					v-for="(group, groupIndex) in groupedBrands"
					:key="group.letter"
					class="flex flex-col gap-24px"
				>
					<!-- Letter Header -->
					<div class="flex gap-24px">
						<div class="typography-h4-str text-n-950">
							{{ group.letter }}
						</div>

						<!-- Brand Grid -->
						<div
							class="grid flex-1 grid-cols-1 gap-24px mdl:grid-cols-2 lg:grid-cols-3"
						>
							<a
								v-for="brand in group.brands"
								:key="brand.slug"
								:href="getBrandUrl(brand)"
								@click="handleBrandClick(brand)"
								target="_blank"
								rel="noopener noreferrer"
								class="typography-b2-reg w-fit break-words text-n-950 !no-underline hover:text-o-500"
							>
								{{ brand.name }}
							</a>
						</div>
					</div>

					<!-- Divider (except after last group) -->
					<div
						v-if="groupIndex < groupedBrands.length - 1"
						class="mb-24px h-[1px] w-full bg-n-100"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
